import React, {
  useLayoutEffect,
  useRef,
  type RefObject,
  type UIEvent,
} from 'react';
import { useTranslation } from 'react-i18next';
import { AutoHideScrollArea } from '@/components/AutoHideScrollArea';
import { EmptyState } from '@/components/EmptyState';
import { PageSpinner } from '@/components/Spinner/PageSpinner';
import { useScrollFadeProgress } from '@/hooks/useScrollFadeProgress';
import { MailCell } from '@/pages/Mail/components/MailCell';
import { MailLoadMore } from '@/pages/Mail/components/MailLoadMore';
import { MAIL_FOLDER_CONFIG } from '@/pages/Mail/mailFolders';
import { MailFolderType } from '@/types/components';
import type { MailMessage } from '@/types/mail';

const FADE_RANGE_PX = 24;

interface Props {
  folderType: MailFolderType;
  mails: MailMessage[];
  isLoading: boolean;
  isSelecting: boolean;
  isMobile: boolean;
  isSearching: boolean;
  hasMorePages: boolean;
  isLoadingMore: boolean;
  selectedIds: Set<string>;
  scrollOffsetRef: RefObject<number>;
  onCycleFollowUp: (id: string) => void;
  onToggleSelect: (id: string) => void;
  onOpen: (id: string) => void;
  onLoadMore: () => void;
}

export const MailList: React.FC<Props> = ({
  folderType,
  mails,
  isLoading,
  isSelecting,
  isMobile,
  isSearching,
  hasMorePages,
  isLoadingMore,
  selectedIds,
  scrollOffsetRef,
  onCycleFollowUp,
  onToggleSelect,
  onOpen,
  onLoadMore,
}) => {
  const { t } = useTranslation();
  const { progress, handleScroll } = useScrollFadeProgress(FADE_RANGE_PX);
  const scrollRef = useRef<HTMLDivElement | null>(null);

  const config = MAIL_FOLDER_CONFIG[folderType];
  const placeholder =
    folderType === MailFolderType.Drafts
      ? t('mail.draftPlaceholder')
      : t('mail.sentPlaceholder');
  const isMobileInbox = isMobile && folderType === MailFolderType.Inbox;
  const emptyKey = isMobileInbox ? 'mail.empty' : 'mail.emptyGeneral';

  const displayName = (mail: MailMessage): string => {
    if (!config.nameFromRecipient) {
      return mail.senderName;
    }

    const base = mail.recipientName ?? placeholder;

    return mail.additionalRecipientCount
      ? `${base} + ${mail.additionalRecipientCount}`
      : base;
  };

  const handleListScroll = (event: UIEvent<HTMLDivElement>) => {
    handleScroll(event);
    scrollOffsetRef.current = event.currentTarget.scrollTop;
  };

  const cells = (
    <>
      {mails.map((mail) => (
        <MailCell
          key={mail.id}
          mail={mail}
          displayName={displayName(mail)}
          showFollowUp={config.showFollowUp}
          showRecipientStatus={config.showRecipientStatus}
          isSelecting={isSelecting}
          isSelected={selectedIds.has(mail.id)}
          isMobile={isMobile}
          onCycleFollowUp={onCycleFollowUp}
          onToggleSelect={onToggleSelect}
          onOpen={onOpen}
        />
      ))}

      <MailLoadMore
        isMobile={isMobile}
        hasMorePages={hasMorePages}
        isLoadingMore={isLoadingMore}
        onLoadMore={onLoadMore}
      />
    </>
  );

  useLayoutEffect(() => {
    const element = scrollRef.current;

    if (!element) {
      return;
    }

    element.scrollTop = scrollOffsetRef.current;
  }, [isLoading, scrollOffsetRef]);

  if (isLoading) {
    return (
      <div className="flex flex-1 items-center justify-center pb-8">
        <PageSpinner className="size-[22px] text-subtle-foreground" />
      </div>
    );
  }

  if (mails.length === 0) {
    return (
      <div className="flex flex-1 flex-col pb-8">
        <EmptyState
          description={t(isSearching ? 'mail.searchEmpty' : emptyKey)}
        />
      </div>
    );
  }

  return (
    <div className="relative flex min-h-0 flex-1 flex-col">
      {isMobile && (
        <div
          style={{ opacity: progress }}
          className="pointer-events-none absolute inset-x-0 top-0 z-10 h-6 bg-gradient-to-b from-constant-black/6 to-transparent transition-opacity duration-300 ease-out"
        />
      )}

      {isMobile && (
        <div
          ref={scrollRef}
          onScroll={handleListScroll}
          className="flex min-h-0 flex-1 flex-col overflow-y-auto"
        >
          {cells}
        </div>
      )}

      {!isMobile && (
        <AutoHideScrollArea
          viewportRef={scrollRef}
          onScroll={handleListScroll}
          className="flex min-h-0 flex-1 flex-col"
        >
          <div className="flex flex-col">{cells}</div>
        </AutoHideScrollArea>
      )}
    </div>
  );
};
